// Admin CRUD: origins (list, add, edit, delete) — configuration / almost constant data

import { useState } from 'react';
import { useOrigins } from '@/hooks/use-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Origin } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

export default function AdminOrigins() {
  const queryClient = useQueryClient();
  const { data: origins, isLoading } = useOrigins();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Origin | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { toast } = useToast();

  const saveMutation = useMutation({
    mutationFn: async () => {
      const row = { name: name.trim(), description: description.trim() || null };
      if (editing) {
        const { error } = await supabase.from('origins').update(row).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('origins').insert(row);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'lookups', 'origins'] });
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: editing ? 'Origin updated' : 'Origin created' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('origins').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'lookups', 'origins'] });
      toast({ title: 'Origin deleted' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  function resetForm() {
    setName('');
    setDescription('');
    setEditing(null);
  }

  function openEdit(origin: Origin) {
    setEditing(origin);
    setName(origin.name);
    setDescription(origin.description ?? '');
    setOpen(true);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Origins</h1>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogTrigger asChild>
          <Button onClick={() => { resetForm(); setEditing(null); }}>Add origin</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit origin' : 'New origin'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Country or region" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" />
            </div>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim()}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      {isLoading ? (
        <p className="text-secondary">Loading...</p>
      ) : (
        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(origins ?? []).map((o) => (
              <TableRow key={o.id}>
                <TableCell>{o.name}</TableCell>
                <TableCell className="text-muted-foreground">{o.description ?? '—'}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(o)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(o.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
